import {
  Box,
  Button,
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  Menu,
  Typography,
} from "@mui/material";
import TextField from "@mui/material/TextField";
import { useState } from "react";
import { useMenu, UseMenuProps } from "../../../../hook/menu";
import { useInsertCronBlock } from "../../../../hook/page/block/cron";
import { SubscriptionSearchResult } from "../../../../types/subscriptionSearch";
import { isValidCronString } from "../../../../util/cron";
import { SubscriptionSelectMenu } from "../../subscription";

export const AddCronMenu: React.FC<
  UseMenuProps & { blockIndex: number; onComplete?: () => void }
> = ({ blockIndex, onComplete, ...useMenuProps }) => {
  const { onClose } = useMenuProps;
  const insertCronBlock = useInsertCronBlock();
  const [name, setName] = useState("Nightly");
  const [schedule, setSchedule] = useState("*/1 * * * *");
  const [selectedSub, setSelectedSub] = useState<SubscriptionSearchResult>();
  const [submitted, setSubmitted] = useState(false);
  const {
    anchorEl,
    handleOpen,
    onClose: closeSubMenu,
    open,
  } = useMenu();

  const validName = name.trim().length > 0;
  const validSchedule = isValidCronString(schedule);
  const validSub = selectedSub !== undefined;
  const canSubmit = validName && validSchedule && validSub;

  const handleSubmit = async () => {
    setSubmitted(true);
    if (!canSubmit) return;
    try {
      await insertCronBlock({
        blockIndex,
        name: name.trim(),
        schedule: schedule.trim(),
        subscription: selectedSub,
      });
    } finally {
      onClose();
      onComplete?.();
    }
  };

  return (
    <>
      <SubscriptionSelectMenu
        anchorEl={anchorEl}
        onClose={closeSubMenu}
        open={open}
        onChange={async (sub) => setSelectedSub(sub)}
      />
      <Menu {...useMenuProps}>
        <Box
          sx={{
            width: "20rem",
            padding: 3,
            gap: 2,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Typography variant="h5">Add Cron</Typography>
          <Typography variant="subtitle1">
            Run a schedule against a subscription block
          </Typography>
          <Divider />
          <FormControl>
            <FormLabel>Name</FormLabel>
            <TextField
              value={name}
              onChange={(e) => setName(e.currentTarget.value)}
              inputProps={{ "data-testid": "cron-name-input" }}
            />
            {submitted && !name.trim() && (
              <FormHelperText error>Name required</FormHelperText>
            )}
          </FormControl>
          <FormControl>
            <FormLabel>Schedule</FormLabel>
            <TextField
              value={schedule}
              onChange={(e) => setSchedule(e.currentTarget.value)}
              inputProps={{ "data-testid": "cron-schedule-input" }}
            />
            {schedule.trim() && !validSchedule && (
              <FormHelperText error>
                Invalid cron expression (e.g. */5 * * * *)
              </FormHelperText>
            )}
            {submitted && !schedule.trim() && (
              <FormHelperText error>Schedule required</FormHelperText>
            )}
          </FormControl>
          <FormControl>
            <FormLabel>Subscription</FormLabel>
            <Button onClick={handleOpen}>
              {selectedSub?.name ?? "Select Subscription"}
            </Button>
            {submitted && !selectedSub && (
              <FormHelperText error>Subscription required</FormHelperText>
            )}
          </FormControl>
          <Button onClick={handleSubmit} variant="outlined" disabled={!canSubmit}>
            Add Cron
          </Button>
        </Box>
      </Menu>
    </>
  );
};
